import * as Sentry from '@sentry/react'
import { getWebinarConnectInfo } from '../global/blocs/useConnectInfo'
import { ENV_SETTINGS } from '../global/envSettings'
import { getFeatures } from '../sharedLegacy/hooks/useFeatures'
import { request } from '../sharedLegacy/utils/api'
import { APIResponse, HTTPMethod } from '../sharedLegacy/utils/api.types'
import { getRoomInfo } from '../global/roomInfo/roomInfo'
import { heartbeatIntegration } from './integrations/heartbeatIntegration'
import { localStorageIntegration } from './integrations/localStorageIntegration'
import { pageLifecycleIntegration } from './integrations/pageLifecycleIntegration'

type TrackingInfoDTO = {
  type: 'target' | 'identity' | 'group'
  name: string
  value: string | number
}[]

const initLogging = async () => {
  if (ENV_SETTINGS.environment === 'development') return
  if (build.SHA_SIGNATURE === 'local') return

  const roomInfo = getRoomInfo()

  // just in case, and it simplifies logic downstream
  if (roomInfo.role === 'audience') return

  const client = Sentry.init({
    dsn: 'https://9e2c5a93d78d759bc23ecea836e44bfc@o198220.ingest.us.sentry.io/4508760594644992',
    release: build.SHA_SIGNATURE,
    environment: ENV_SETTINGS.environment,
    initialScope: {
      user: {
        id: 'speakerId' in roomInfo ? roomInfo.speakerId : undefined,
      },
      tags: {
        liveId: roomInfo.liveId,
        role: roomInfo.role,
        isSecondCam: roomInfo.isSecondCam,
      },
    },
    normalizeDepth: 10,
    integrations: [
      Sentry.captureConsoleIntegration(),
      Sentry.extraErrorDataIntegration({ depth: 10 }),
      Sentry.featureFlagsIntegration(),
      Sentry.httpClientIntegration(),
      Sentry.reportingObserverIntegration(),
      pageLifecycleIntegration(),
      localStorageIntegration(),
    ],
  })

  getWebinarConnectInfo().then((webinarInfo) => {
    if (webinarInfo.streaming_method !== 'hardware') {
      Sentry.addIntegration(heartbeatIntegration())
    }
  })

  if (roomInfo.role === 'producer') {
    request<void, APIResponse<TrackingInfoDTO>>({
      url: '/api/2/user/tracking',
      method: HTTPMethod.GET,
    }).then((response) => {
      const { userId, email, ...user } = response.data.reduce(
        (acc, { type, name, value }) => {
          if (['identity', 'group'].includes(type))
            return { ...acc, [name]: value }
          return acc
        },
        {} as { userId?: number; email?: string } & Record<string, any>
      )

      Sentry.setUser({ id: userId, email, display: userId })
      Sentry.setTags(user)
    })
  } else {
    Sentry.setUser({ id: roomInfo.speakerId })
  }

  getWebinarConnectInfo().then((connectInfo) => {
    Sentry.setTag('webrtc_provider', connectInfo.webrtc_provider)
    Sentry.setTag('webrtc_identity', connectInfo.webrtc_identity)
    Sentry.setTag('streaming_method', connectInfo.streaming_method)
    Sentry.setTag('locale', connectInfo.locale)
  })

  const flagsIntegration =
    client?.getIntegrationByName<Sentry.FeatureFlagsIntegration>('FeatureFlags')
  if (flagsIntegration) {
    getFeatures().then((features) => {
      Object.entries(features).forEach(([k, v]) => {
        flagsIntegration.addFeatureFlag(k, v)
      })
    })
  }
}

const logMessage = Sentry.captureMessage
const logError = Sentry.captureException
const logEvent = Sentry.captureEvent
const logBreadcrumb = Sentry.addBreadcrumb

export { initLogging, logMessage, logError, logEvent, logBreadcrumb }
